import { DatePipe } from '@angular/common';
import {
    Component,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
    ApiDefaults,
    ApiService,
    FilterRequest,
    ListResponse
} from '@lsq-frontend/angular-shared/api-service';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';
import {
    AnalyticsEvent,
    AnalyticsProp,
    AnalyticsService
} from '@lsq-frontend/angular-shared/features/analytics';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import {
    AsOfFilterRequest,
    DateRangeFilterRequest,
    FileType,
    GenerateReportRequest,
    ShareReportRequest,
    VoidResponse
} from '@lsq-frontend/remote-services/enterprise/common';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { cloneDeep } from 'lodash';
import { Subscription } from 'rxjs';
import { Color } from '../../../constants/color.constants';
import { DateFormat } from '../../../constants/date-format.constants';
import { Form } from '../../../constants/form.constants';
import { Icons } from '../../../constants/icons.constants';
import { IOptionalExportServiceConfig } from '../../../models/common';
import { DownloadService } from '../../../services/api/download.service';
import { Serializer } from '../../../services/serializer/serializer';
import { forLifeOf, LifecycleAware } from '../../../utilities/rxjs.helpers';
import { LsqToggleDropdownComponent } from '../toggle-dropdown/lsq-toggle-dropdown.component';

@LifecycleAware()
@Component({
    selector: 'lsq-export-form',
    templateUrl: './lsq-export-form.component.html',
    styleUrls: ['./lsq-export-form.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class LsqExportFormComponent implements OnInit, OnDestroy {
    @Input() public subTitle?: string;
    @Input() public reportName: string;
    @Input() public analyticsName?: string;
    @Input() public service: ApiService<DateRangeFilterRequest | FilterRequest, ListResponse<any>>;
    @Input() public optionalExportApiServiceConfig?: IOptionalExportServiceConfig;
    @Input() public shareService: ApiService<ShareReportRequest, VoidResponse>;
    @Input() public shareServiceFeatureFlag: number;
    @ViewChild('dropdownRef') public dropdownRef: LsqToggleDropdownComponent;

    public display = false;
    public formGroup: FormGroup;
    public icons = Icons;
    public colors = Color;
    public dateStringSuffix: string;
    public isLoadingReport = false;
    public formGroupEmail: FormGroup;
    public recipients: string[] = [];
    public request: DateRangeFilterRequest | FilterRequest;
    public optionalRequest: DateRangeFilterRequest | FilterRequest;
    private requestSubscription: Subscription;
    private downloadSubscription: Subscription;
    public exportTimeout: any;
    public useOptionalService = false;

    public toLabel = this._i18n({
        value: 'to',
        meaning: 'to in date range {date} to {date}'
    });
    public asOfLabel = this._i18n({
        value: 'As of',
        meaning: 'As of in date label "As of {date}"'
    });
    public prefix = this._i18n({
        value: 'Report as',
        meaning: 'Report as in export option in "Report as {filetype}"'
    });
    public options = [
        { id: FileType.Pdf, label: `${this.prefix} PDF`, value: FileType.Pdf },
        { id: FileType.Csv, label: `${this.prefix} CSV`, value: FileType.Csv }
    ];
    public generationSuccess = this._i18n({
        value: 'Your report has been successfully generated.',
        meaning: 'Success message when report is generated'
    });
    public generationWaitMessage = this._i18n({
        value:
            'This can take several minutes. You can continue using the application, but refrain from closing/refreshing the page.',
        meaning: 'Notification for when report is taking long to generate'
    });
    public generationWaitMessageTitle = this._i18n({
        value: 'Report Generating',
        meaning: 'Title for notification for when report is taking long to generate'
    });

    constructor(
        private _analyticsService: AnalyticsService,
        private _datePipe: DatePipe,
        private _downloadService: DownloadService,
        private _serializer: Serializer,
        private _alertService: AlertService,
        private _userService: UserService,
        private _i18n: I18n,
        @Inject(SETTINGS) private _settings: ISettings
    ) {}

    public ngOnInit(): void {
        this.formGroup = new FormGroup({
            select: new FormControl(FileType.Pdf, Validators.required)
        });

        this.formGroupEmail = new FormGroup({
            email: new FormControl('', [Validators.required, Validators.email])
        });

        // TODO(tbottonari): Add an `errorStream` to DownloadService and subscribe to that instead.
        this._alertService.errorStream.pipe(forLifeOf(this)).subscribe(() => {
            this.setLoadingReport(false);
            this.close();
        });
    }

    public ngOnDestroy(): void {}

    public subscribeToRequestStream(isOptionalService = false): void {
        if (isOptionalService) {
            return;
        } else {
            if (!!this.requestSubscription) {
                this.requestSubscription.unsubscribe();
            }
        }
        const service = isOptionalService
            ? this.optionalExportApiServiceConfig.service
            : this.service;
        this.requestSubscription = service.requestStream
            .pipe(forLifeOf(this))
            .subscribe((requestObject) => {
                this.generateRequest(requestObject, isOptionalService);
            });
    }

    public generateRequest(requestObject: any, isOptionalService: boolean): void {
        const newRequest = cloneDeep(requestObject); // Deep clone so that the request is not accidently changed.
        const dateStringDashed = DateFormat.LsqShortYearDashed;
        const dateStringSlashed = DateFormat.LsqShortYear;
        let tempRequest = newRequest;

        // Add company key to new request.
        newRequest.companyKey = this._userService.getCompanyKey();

        if (!!(newRequest as GenerateReportRequest).endpointName) {
            tempRequest = this._serializer.deserialize<DateRangeFilterRequest | FilterRequest>(
                (newRequest as GenerateReportRequest).jsonRequest
            );
        }

        if (!!tempRequest && !!(tempRequest as DateRangeFilterRequest).fromDateFilter) {
            const filterRequest = tempRequest as DateRangeFilterRequest;
            let fromDate = this._datePipe.transform(
                filterRequest.fromDateFilter,
                dateStringSlashed
            );
            let toDate = this._datePipe.transform(filterRequest.toDateFilter, dateStringSlashed);
            if (fromDate && toDate) {
                this.subTitle = fromDate + ' ' + this.toLabel + ' ' + toDate;

                fromDate = this._datePipe.transform(filterRequest.fromDateFilter, dateStringDashed);
                toDate = this._datePipe.transform(filterRequest.toDateFilter, dateStringDashed);

                this.dateStringSuffix = `_${fromDate}_to_${toDate}`;
            }
        } else if (!!tempRequest && !!(tempRequest as AsOfFilterRequest).asOfDateFilter) {
            const filterRequest = tempRequest as AsOfFilterRequest;
            let date = this._datePipe.transform(filterRequest.asOfDateFilter, dateStringSlashed);

            if (date) {
                this.subTitle = this.asOfLabel + ' ' + date;
                date = this._datePipe.transform(filterRequest.asOfDateFilter, dateStringDashed);
                this.dateStringSuffix = `_as_of_${date}`;
            }
        } else {
            this.subTitle = '';
            this.dateStringSuffix = '';
        }
        if (isOptionalService) {
            this.optionalRequest = newRequest;
            this.optionalRequest.skip = 0;
            this.optionalRequest.take = ApiDefaults.TakeAll;
        } else {
            this.request = newRequest;
            this.request.skip = 0;
            this.request.take = ApiDefaults.TakeAll;
        }
    }

    public onOpen(): void {
        // Subscribe when the export dropdown opens.
        this.subscribeToRequestStream();
        if (!!this.optionalExportApiServiceConfig) {
            this.subscribeToRequestStream(true);
        }
        this.display = true;
    }

    public close(): void {
        this.dropdownRef.closeDropdown();
        this.formGroup.reset({
            select: FileType.Pdf
        });
        this.formGroupEmail.reset();
        this.recipients = [];
        this.useOptionalService = false;
    }

    public handleSubmit(e: Event): void {
        e.preventDefault();
        const exportType = this.formGroup.controls.select.value;

        if (exportType != null) {
            if (!!this.request) {
                ((this.request as unknown) as GenerateReportRequest).fileType = exportType;
            }
            if (
                !!this.optionalExportApiServiceConfig &&
                !!this.optionalExportApiServiceConfig.dataSource &&
                !!this.optionalExportApiServiceConfig.dataSource.request
            ) {
                this.generateRequest(
                    new GenerateReportRequest({
                        endpointName: this.optionalExportApiServiceConfig.endpointName,
                        jsonRequest: this._serializer.serialize({
                            ...this.optionalExportApiServiceConfig.dataSource.request,
                            companyKey: this._userService.getCompanyKey()
                        })
                    }),
                    true
                );
                ((this.optionalRequest as unknown) as GenerateReportRequest).fileType = exportType;
            }

            // If generating the report takes more than 10 sec, close and show the info toast.
            this.exportTimeout = setTimeout(
                () => {
                    this.showGeneratingMessage();
                    this.close();
                },
                this._settings.downloadBackgroundTimeout
                    ? this._settings.downloadBackgroundTimeout
                    : 10 * 1000
            ); // Use default just in case the setting is not set;

            let service = this.service;
            let request = this.request;
            if (this.useOptionalService) {
                service = this.optionalExportApiServiceConfig.service;
                request = this.optionalRequest;
            }
            if (this.downloadSubscription) {
                this.downloadSubscription.unsubscribe();
            }
            this.downloadSubscription = this._downloadService
                .execute(service, request, exportType, this.getFileName())
                .pipe(forLifeOf(this))
                .subscribe((blob: Blob) => {
                    this.setLoadingReport(false);
                    if (!this.display) {
                        // If the dropdown is already closed, show a success toast.
                        this._alertService.success(this.generationSuccess);
                    }
                    this.close();
                });

            this.setLoadingReport(true);

            if (this.analyticsName) {
                this._analyticsService.triggerEventWithPayload(AnalyticsEvent.Export, {
                    [AnalyticsProp.ReportName]: this.analyticsName,
                    [AnalyticsProp.FileType]: FileType[exportType]
                });
            }
        }
    }

    private showGeneratingMessage(): void {
        this._alertService.info(this.generationWaitMessage, {
            title: this.generationWaitMessageTitle,
            config: {
                disableTimeOut: true
            }
        });
    }

    private setLoadingReport(value: boolean): void {
        if (value) {
            this.formGroup.get('select').disable();
        } else {
            this.formGroup.get('select').enable();
            clearTimeout(this.exportTimeout);
        }
        this.isLoadingReport = value;
    }

    public getFileName(): string {
        const modifiedReportName = this.reportName
            .replace(new RegExp(/\s/, 'g'), '-')
            .replace(new RegExp(/[\_]{2,}/, 'g'), '-')
            .replace(new RegExp(/[^a-zA-Z0-9/\-\_]+/, 'g'), '');

        return this.dateStringSuffix && this.dateStringSuffix.length > 0
            ? `${modifiedReportName}_${this.dateStringSuffix}`
            : `${modifiedReportName}`;
    }

    public isExportOptionSelected(): boolean {
        if (this.formGroup && this.formGroup.controls) {
            return this.formGroup.controls.select.status === Form.Invalid;
        }
        return true;
    }

    public handleSend(e: Event): void {
        e.preventDefault();
        const exportType = this.formGroup.controls.select.value;
        if (exportType != null) {
            const shareRequest = new ShareReportRequest({
                endpointName: this.service.endpointName,
                contentType: this.formGroup.controls.select.value,
                recipients: this.recipients,
                reportName: this.getFileName(),
                jsonRequest: this._serializer.serialize(this.request)
            });

            this.shareService.execute(shareRequest);

            if (this.analyticsName) {
                this._analyticsService.triggerEventWithPayload(AnalyticsEvent.EmailExport, {
                    [AnalyticsProp.ReportName]: this.analyticsName,
                    [AnalyticsProp.FileType]: FileType[exportType]
                });
            }
        }
        this.close();
    }

    public handleAddRecipient(e: Event): void {
        e.preventDefault();
        const input = this.formGroupEmail.controls.email.value;

        if (this.formGroupEmail.controls.email.valid && this.recipients.length < 10) {
            this.recipients.push(input);
            this.formGroupEmail.controls.email.reset();
        }
    }

    public handleRemoveRecipient(index: number, e: Event): void {
        e.preventDefault();
        this.recipients.splice(index, 1);
    }
}
